import { supabase } from './supabaseClient';

export const WORK_TYPES = [
  { id: 'screed', name: 'Стяжка підлоги', available: true },
  { id: 'plaster', name: 'Штукатурка стін', available: true },
  { id: 'wall_prep', name: 'Малярні роботи - стіни', available: true },
  { id: 'ceiling_prep', name: 'Малярні роботи - стеля', available: true },
  { id: 'partition', name: 'Перегородки ГКЛ', available: true },
  { id: 'cladding', name: 'Облицювання стін ГКЛ', available: true },
  { id: 'gkl_construct', name: 'Конструкції з ГКЛ', available: true },
  { id: 'ceiling', name: 'Підвісні стелі ГКЛ', available: true },
  { id: 'masonry', name: 'Мурування стін', available: true },
  { id: 'tiling', name: 'Плиточні роботи', available: true },
  { id: 'waterproof', name: 'Гідроізоляція', available: false },
  { id: 'drywall', name: 'Гіпсокартон', available: false },
  { id: 'insulation', name: 'Утеплення', available: false },
];

let _cache = { tags: null, products: null, technologies: null, productsByType: null, timestamp: 0 };
const CACHE_TTL = 5 * 60 * 1000;

function isCacheValid() {
  return _cache.tags && _cache.products && _cache.technologies && (Date.now() - _cache.timestamp < CACHE_TTL);
}

export async function fetchTags(workType = 'screed') {
  if (_cache.tags) return _cache.tags[workType] || [];
  const { data, error } = await supabase.from('calc_tags').select('*').order('sort_order');
  if (error) { console.error('fetchTags:', error); return []; }
  _cache.tags = {};
  data.forEach(tag => {
    if (!_cache.tags[tag.work_type]) _cache.tags[tag.work_type] = [];
    _cache.tags[tag.work_type].push({ id: tag.id, label: tag.label });
  });
  return _cache.tags[workType] || [];
}

export async function fetchProducts() {
  if (_cache.products) return _cache.products;
  const { data, error } = await supabase.from('calc_products').select('*').order('manufacturer');
  if (error) { console.error('fetchProducts:', error); return []; }
  _cache.products = data;
  _cache.productsByType = {};
  data.forEach(p => {
    if (!_cache.productsByType[p.product_type]) _cache.productsByType[p.product_type] = [];
    _cache.productsByType[p.product_type].push({
      id: p.id, name: p.name, manufacturer: p.manufacturer,
      consumption: p.consumption ? Number(p.consumption) : null,
      consumptionPer10mm: p.consumption_per_10mm ? Number(p.consumption_per_10mm) : null,
      unit: p.unit, packaging: Number(p.packaging),
      packagingUnit: p.packaging_unit, source: p.source_url || '',
    });
  });
  return _cache.products;
}

export function getProductsByType(productType) {
  return (_cache.productsByType && _cache.productsByType[productType]) || [];
}

export async function fetchTechnologies(workType = 'screed') {
  if (_cache.technologies) return _cache.technologies[workType] || [];
  const { data, error } = await supabase.from('calc_technologies').select('*').order('sort_order');
  if (error) { console.error('fetchTechnologies:', error); return []; }
  _cache.technologies = {};
  data.forEach(tech => {
    if (!_cache.technologies[tech.work_type]) _cache.technologies[tech.work_type] = [];
    const layers = (tech.layers || []).map(l => ({
      id: l.id, name: l.name, productType: l.product_type,
      hasThickness: l.has_thickness || false, defaultThickness: l.default_thickness || null,
      calcType: l.calc_type || 'area', defaultProduct: l.default_product || null,
      note: l.note || null, required: true, enabled: true,
      gklLayers: l.gkl_layers || null,
      optional: l.optional || false,
      minGklLayers: l.min_gkl_layers || null,
      mortarMode: l.mortar_mode || null,
      lintelMode: l.lintel_mode || null,
      coats: l.coats || null,
    }));
    const consumables = (tech.consumables || []).map(c => ({
      name: c.name, unit: c.unit, qty: c.qty || 0,
      calcType: c.calc_type, consumption: c.consumption || null, formula: c.formula || null, condition: c.condition || null,
    }));
    _cache.technologies[tech.work_type].push({
      id: tech.id, name: tech.name, description: tech.description,
      tags: tech.tags || [], author: tech.brand, brand: tech.brand,
      verified: tech.verified, authorId: tech.author_id,
      layers, consumables, tools: tech.tools || [], notes: tech.notes || '',
    });
  });
  return _cache.technologies[workType] || [];
}

export async function loadCalcData(workType = 'screed') {
  if (isCacheValid()) {
    return { tags: _cache.tags[workType] || [], products: _cache.products, technologies: _cache.technologies[workType] || [] };
  }
  const [tags, products, technologies] = await Promise.all([
    fetchTags(workType), fetchProducts(), fetchTechnologies(workType),
  ]);
  _cache.timestamp = Date.now();
  return { tags, products, technologies };
}

export function clearCalcCache() {
  _cache = { tags: null, products: null, technologies: null, productsByType: null, timestamp: 0 };
}

function evalFormula(formula, area, perimeter, extraVars = {}) {
  if (!formula) return 0;
  try {
    const vars = {
      area, perimeter,
      outerCorners: 0, innerCorners: 0, height: 0,
      openingsArea: 0, doors: 0, doorW: 0.9, windows: 0,
      windowsLm: 0, outerCornersTotalLm: 0,
      nichesLength: 0, ceilPerimeter: 0, ceilLength: 0, ceilWidth: 0,
      ptLength: 0, tilesPerM2: 0, rebarQty: 0, totalOpenings: 0,
      hangerQty: 0, udTotal: 0,
      ...extraVars,
    };
    let expr = formula;
    // Сортуємо ключі по довжині (довші першими) щоб уникнути часткового replace
    const keys = Object.keys(vars).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      expr = expr.replace(new RegExp(key, 'g'), String(vars[key] || 0));
    }
    return Math.ceil(Function('"use strict"; return (' + expr + ')')());
  } catch { return 0; }
}

// Перевірка чи технологія — водяна тепла підлога (по тегу)
function isWarmWaterTech(technology) {
  return (technology?.tags || []).includes('warm_floor_water');
}

// Типи шарів стяжки (productType які є стяжкою)
const SCREED_PRODUCT_TYPES = ['screed_mix', 'light_screed_mix', 'semi_dry_mix', 'self_leveling_mix'];

function isScreedLayer(layer) {
  return SCREED_PRODUCT_TYPES.includes(layer.productType);
}

export function calculateMaterials(technology, dimensions, productSelections = {}, layerThicknesses = {}, layerToggles = {}, pipeParams = {}, workType = 'screed') {
  if (!technology) return { layers: [], consumables: [], tools: [], area: 0, perimeter: 0, pipeLength: 0, workType };
  let area, perimeter, length, width;

  if (workType === 'ceiling_prep') {
    // Малярні роботи стеля: площа + периметр + ніші
    const ceilArea = parseFloat(dimensions.ceilArea) || 0;
    const ceilPerimeter = parseFloat(dimensions.ceilPerimeter) || 0;
    const niches = dimensions.niches || [];
    // Ніші: площа торця = довжина × глибина, додається до загальної площі
    const nichesArea = niches.reduce((sum, n) => {
      const l = parseFloat(n.length) || 0;
      const d = parseFloat(n.depth) || 0;
      return sum + l * d;
    }, 0);
    // Довжина кутників = сума довжин ніш
    const nichesLength = niches.reduce((sum, n) => sum + (parseFloat(n.length) || 0), 0);
    area = ceilArea + nichesArea;
    perimeter = ceilPerimeter;
    length = ceilPerimeter;
    width = 0;
    dimensions._innerCorners = 0;
    dimensions._outerCorners = 0;
    dimensions._height = 0;
    dimensions._doors = 0;
    dimensions._windows = 0;
    dimensions._openingDepth = 0;
    dimensions._openingsArea = 0;
    dimensions._nichesLength = nichesLength;
    dimensions._ceilPerimeter = ceilPerimeter;
  } else if (workType === 'plaster' || workType === 'wall_prep' || workType === 'cladding' || workType === 'masonry') {
    // Штукатурка / підготовка стін / мурування: розрахунок площі стін
    const height = parseFloat(dimensions.height) || 0;
    const wallLength = parseFloat(dimensions.wallLength) || 0;
    const innerCorners = parseInt(dimensions.innerCorners) || 0;
    const outerCorners = parseInt(dimensions.outerCorners) || 0;
    const openingDepth = parseFloat(dimensions.openingDepth) || 0.15;

    // Підтримка обох форматів: masonry — масиви, інші — числа
    let doors, doorW, doorH, windows, winW, winH, doorOpeningsList, windowOpeningsList;
    if (workType === 'masonry' && Array.isArray(dimensions.doorOpenings)) {
      doorOpeningsList = dimensions.doorOpenings || [];
      windowOpeningsList = dimensions.windowOpenings || [];
      doors = doorOpeningsList.length;
      doorW = doorOpeningsList[0] ? (parseFloat(doorOpeningsList[0].w) || 0.9) : 0.9;
      doorH = doorOpeningsList[0] ? (parseFloat(doorOpeningsList[0].h) || 2.1) : 2.1;
      windows = windowOpeningsList.length;
      winW = windowOpeningsList[0] ? (parseFloat(windowOpeningsList[0].w) || 1.5) : 1.5;
      winH = windowOpeningsList[0] ? (parseFloat(windowOpeningsList[0].h) || 1.4) : 1.4;
    } else {
      // plaster / wall_prep / cladding — читаємо масиви doorOpenings/windowOpenings
      doorOpeningsList = Array.isArray(dimensions.doorOpenings) ? dimensions.doorOpenings : [];
      windowOpeningsList = Array.isArray(dimensions.windowOpenings) ? dimensions.windowOpenings : [];
      doors = doorOpeningsList.length;
      doorW = doorOpeningsList[0] ? (parseFloat(doorOpeningsList[0].w) || 0.9) : 0.9;
      doorH = doorOpeningsList[0] ? (parseFloat(doorOpeningsList[0].h) || 2.1) : 2.1;
      windows = windowOpeningsList.length;
      winW = windowOpeningsList[0] ? (parseFloat(windowOpeningsList[0].w) || 1.5) : 1.5;
      winH = windowOpeningsList[0] ? (parseFloat(windowOpeningsList[0].h) || 1.4) : 1.4;
    }

    const grossArea = height * wallLength;
    // Площа дверей: сума всіх отворів
    const doorArea = doorOpeningsList.length > 0
      ? doorOpeningsList.reduce((s, o) => s + (parseFloat(o.w)||0) * (parseFloat(o.h)||0), 0)
      : 0;
    // Площа вікон: сума всіх отворів
    const windowArea = windowOpeningsList.length > 0
      ? windowOpeningsList.reduce((s, o) => s + (parseFloat(o.w)||0) * (parseFloat(o.h)||0), 0)
      : 0;
    area = Math.max(grossArea - doorArea - windowArea, 0);
    perimeter = wallLength;
    length = wallLength;
    width = height;

    // Довжина відкосів вікон: для кожного вікна верх + 2 боки (winW + winH*2)
    const windowsLm = windowOpeningsList.reduce((s, o) => {
      const w = parseFloat(o.w) || 0;
      const h = parseFloat(o.h) || 0;
      return s + w + h * 2;
    }, 0);

    // Зовнішні кутники: вручну + відкоси вікон
    const outerCornersManualLm = outerCorners * height;
    const outerCornersTotalLm = outerCornersManualLm + windowsLm;

    // Зберігаємо додаткові дані для consumables
    dimensions._innerCorners = innerCorners;
    dimensions._outerCorners = outerCorners;
    dimensions._height = height;
    dimensions._doors = doors;
    dimensions._doorW = doorW;
    dimensions._doorH = doorH;
    dimensions._windows = windows;
    dimensions._winW = winW;
    dimensions._winH = winH;
    dimensions._openingDepth = openingDepth;
    dimensions._doorOpeningsList = doorOpeningsList;
    dimensions._windowOpeningsList = windowOpeningsList;
    dimensions._windowsLm = windowsLm;           // п.м. відкосів вікон
    dimensions._outerCornersTotalLm = outerCornersTotalLm; // п.м. всіх зовнішніх кутів

    // Площа відкосів (для інших розрахунків)
    const windowOpeningsPerimeter = windowsLm; // вже порахований
    const doorOpeningsPerimeter = doorOpeningsList.reduce((s, o) => {
      const w = parseFloat(o.w) || 0;
      const h = parseFloat(o.h) || 0;
      return s + h * 2 + w;
    }, 0);
    dimensions._openingsArea = (doorOpeningsPerimeter + windowOpeningsPerimeter) * openingDepth;
    dimensions._windowOpeningsArea = windowOpeningsPerimeter * openingDepth;

    // wall_prep: додаємо площу віконних відкосів (дверні не шпаклюються — там коробка)
    if (workType === 'wall_prep') {
      area += dimensions._windowOpeningsArea;
    }
  } else if (workType === 'partition') {
    // Перегородки: висота × довжина, мінус дверні прорізи
    const height = parseFloat(dimensions.height) || 2.7;
    const partLength = parseFloat(dimensions.partitionLength) || 0;
    const doorOpeningsPt = dimensions.doorOpenings || [];
    const doorArea = doorOpeningsPt.reduce((s,o) => s + (parseFloat(o.w)||0)*(parseFloat(o.h)||0), 0);
    const doors = doorOpeningsPt.length;
    const doorW = doorOpeningsPt[0] ? (parseFloat(doorOpeningsPt[0].w)||0) : 0;
    const doorH = doorOpeningsPt[0] ? (parseFloat(doorOpeningsPt[0].h)||0) : 0;
    const grossArea = height * partLength;
    area = Math.max(grossArea - doorArea, 0);
    perimeter = 2 * (height + partLength);
    length = partLength;
    width = height;

    // Зберігаємо для динамічних consumables
    dimensions._ptHeight = height;
    dimensions._ptLength = partLength;
    dimensions._ptDoors = doors;
    dimensions._ptDoorW = doorW;
    dimensions._ptDoorH = doorH;
  } else if (workType === 'gkl_construct') {
    // Конструкції з ГКЛ — будуємо список площин
    const techTags = technology.tags || [];
    const sA = parseFloat(dimensions.columnA) || 0;
    const sB = parseFloat(dimensions.columnB) || 0;
    const h = parseFloat(dimensions.height) || 0;
    const boxLen = parseFloat(dimensions.boxLength) || 0;
    const depth = parseFloat(dimensions.gcDepth) || 0;
    const gcW = parseFloat(dimensions.gcWidth) || 0;
    const nicheSides = parseInt(dimensions.nicheSides) || 3;
    const shelfCount = parseInt(dimensions.shelfCount) || 1;
    const shelfLen = parseFloat(dimensions.shelfLength) || 0;
    const shelfDepth = parseFloat(dimensions.shelfDepth) || 0;

    // planes: [{w, h, type}] — w=ширина площини, h=висота/довжина площини, type='box'|'wall'
    // type='box' — UD рамкою, CD за правилом ≥500, без підвісів
    // type='wall' — логіка облицювання: UD по периметру, CD з кроком, підвіси
    let planes = [];

    if (techTags.includes('gc_column')) {
      // Колона: 4 сторони
      planes = [
        { w: sA, h: h, type: 'box' }, { w: sA, h: h, type: 'box' },
        { w: sB, h: h, type: 'box' }, { w: sB, h: h, type: 'box' },
      ];
    } else if (techTags.includes('gc_box_p')) {
      // П-короб: 3 сторони (фронт + 2 бічні)
      planes = [
        { w: sA, h: boxLen, type: 'box' },
        { w: sB, h: boxLen, type: 'box' }, { w: sB, h: boxLen, type: 'box' },
      ];
    } else if (techTags.includes('gc_box_g')) {
      // Г-короб: 2 сторони
      planes = [
        { w: sA, h: boxLen, type: 'box' },
        { w: sB, h: boxLen, type: 'box' },
      ];
    } else if (techTags.includes('gc_install')) {
      // Інсталяція: П-короб + кришка
      planes = [
        { w: gcW, h: h, type: 'box' },                    // фронт
        { w: depth, h: h, type: 'box' },                   // бічна
        { w: depth, h: h, type: 'box' },                   // бічна
        { w: gcW, h: depth, type: 'box' },                  // кришка зверху
      ];
    } else if (techTags.includes('gc_box_h')) {
      // Г-короб горизонтальний: стіна + кришка
      planes = [
        { w: gcW, h: h, type: 'wall' },                    // стіна (облицювання)
        { w: gcW, h: depth, type: 'box' },                  // кришка
      ];
    } else if (techTags.includes('gc_niche')) {
      // Ніша: 2 бічні + верх + (опційно низ)
      planes = [
        { w: depth, h: h, type: 'box' }, { w: depth, h: h, type: 'box' }, // бічні
        { w: gcW, h: depth, type: 'box' },                                  // верх
      ];
      if (nicheSides === 4) planes.push({ w: gcW, h: depth, type: 'box' }); // низ
    } else if (techTags.includes('gc_shelf')) {
      // Полиці: кожна полиця як горизонтальна перегородка (рахується далі в calc_type)
      // area = площа верх+низ однієї полиці × кількість (без торця, торець додається в gkl)
      planes = []; // полиці рахуються окремою логікою
    }

    // Зберігаємо площини для calc_type
    dimensions._gcPlanes = planes;
    dimensions._gcTechTags = techTags;
    dimensions._gcNicheSides = nicheSides;
    dimensions._gcShelfCount = shelfCount;
    dimensions._gcShelfLen = shelfLen;
    dimensions._gcShelfDepth = shelfDepth;
    dimensions._gcDepth = depth;
    dimensions._gcWidth = gcW;
    dimensions._gcHeight = h;

    // Загальна площа обшивки
    area = planes.reduce((sum, p) => sum + p.w * p.h, 0);
    // Для полиць: верх + низ × кількість
    if (techTags.includes('gc_shelf')) {
      area = shelfLen * shelfDepth * 2 * shelfCount;
    }
    perimeter = 0;
    length = 0;
    width = 0;
  } else if (workType === 'tiling') {
    // Плиточні роботи: користувач вводить площу напряму
    area = parseFloat(dimensions.tilingArea) || 0;
    perimeter = 0;
    length = 0;
    width = 0;
    // Зберігаємо параметри для dynamic consumables
    dimensions._tilingType      = dimensions.tilingType || 'standard'; // 'standard' | 'mosaic'
    dimensions._tilingWarmFloor = dimensions.tilingWarmFloor || false;
    dimensions._hydroArea       = parseFloat(dimensions.hydroArea) || 0;
    dimensions._outerCorners    = parseFloat(dimensions.outerCorners) || 0;
    dimensions._innerCorners    = parseFloat(dimensions.innerCorners) || 0;
    dimensions._tileW           = parseFloat(dimensions.tileW) || 0;   // мм
    dimensions._tileH           = parseFloat(dimensions.tileH) || 0;   // мм
    dimensions._groutWidth      = parseFloat(dimensions.groutWidth) || 2; // мм
    dimensions._svpMode         = dimensions.svpMode || 'crosses';     // 'crosses' | 'svp'
    dimensions._holes           = dimensions.holes || [];               // [{label, diameter}]
  } else {
    // Стяжка / стеля: довжина × ширина
    length = parseFloat(dimensions.length) || 0;
    width = parseFloat(dimensions.width) || 0;
    area = length * width;
    perimeter = 2 * (length + width);
    if (workType === 'ceiling') {
      dimensions._ceilLength = length;
      dimensions._ceilWidth = width;
      dimensions._ceilDrop = (parseFloat(dimensions.ceilDrop) || 100) / 1000; // мм → м
    }
  }

  if (area === 0) return { layers: [], consumables: [], tools: [], area, perimeter, pipeLength: 0, workType };

  // Параметри труби для водяної ТП
  const hasWarmWater = isWarmWaterTech(technology);
  const pipeDiameter = hasWarmWater ? (parseFloat(pipeParams.diameter) || 16) : 0; // мм
  const pipeStep = hasWarmWater ? (parseFloat(pipeParams.step) || 150) : 0; // мм
  const pipeLength = hasWarmWater ? Math.ceil((width / (pipeStep / 1000)) * length) : 0; // м.п.
  const pipeAreaInSection = hasWarmWater ? pipeLength * (pipeDiameter / 1000) * 0.8 : 0; // м²

  // Для маяків штукатурки: кількість = довжина стіни / крок (1.2м) + 1
  const beaconCount = (workType === 'plaster' || workType === 'wall_prep' || workType === 'ceiling_prep')
    ? Math.ceil((parseFloat(dimensions.wallLength) || 0) / 1.2) + 1
    : Math.ceil(width / 1) + 1;

  const calculatedLayers = technology.layers.map(layer => {
    const isEnabled = layerToggles[layer.id] !== undefined ? layerToggles[layer.id] : layer.enabled;

    // Masonry mortar mode: пропускаємо layers з невідповідним mode
    if (layer.mortarMode) {
      const currentMode = dimensions.mortarMode || 'ready';
      if (layer.mortarMode !== currentMode) {
        return { ...layer, qty: 0, qtyRounded: 0, product: null, products: [], packs: 0, packagingUnit: '', packagingSize: 0, unit: '', thickness: null, isEnabled: false, hidden: true };
      }
    }
    // Masonry lintel mode
    if (layer.lintelMode) {
      const currentLintel = dimensions.lintelMode || 'aeroc';
      if (layer.lintelMode !== currentLintel) {
        return { ...layer, qty: 0, qtyRounded: 0, product: null, products: [], packs: 0, packagingUnit: '', packagingSize: 0, unit: '', thickness: null, isEnabled: false, hidden: true };
      }
    }

    // Partition: резолвимо auto_ product_type на основі обраного профілю
    let resolvedProductType = layer.productType;

    // Tiling: клей — всі типи разом, або тільки elastic при теплій підлозі
    if (workType === 'tiling' && layer.productType === 'tile_adhesive') {
      const isWarm = !!dimensions._tilingWarmFloor;
      resolvedProductType = isWarm ? '__merged_tile_adhesive_warm__' : '__merged_tile_adhesive_all__';
    }

    if (workType === 'partition' || workType === 'cladding' || workType === 'gkl_construct' || workType === 'ceiling' || workType === 'masonry') {
      // Визначаємо ширину профілю з обраного UW продукту або dims
      const ptW = dimensions.ptWidth || '75';
      if (layer.productType === 'auto_uw') {
        // Всі UW профілі — 50/75/100
        resolvedProductType = '__merged_uw__';
      } else if (layer.productType === 'auto_cw') {
        // CW відповідної ширини (синхронізується з UW)
        resolvedProductType = 'profile_cw_' + ptW;
      } else if (layer.productType === 'auto_gkl') {
        resolvedProductType = '__merged_gkl__';
      } else if (layer.productType === 'auto_insulation') {
        resolvedProductType = '__merged_insulation__';
      } else if (layer.productType === 'auto_board') {
        resolvedProductType = '__merged_board__';
      } else if (layer.productType === 'auto_shelf_guide') {
        // Полиці: всі направляючі (UD + UW)
        resolvedProductType = '__merged_shelf_guide__';
      } else if (layer.productType === 'auto_shelf_stud') {
        // Полиці: всі несучі (CD + CW)
        resolvedProductType = '__merged_shelf_stud__';
      } else if (layer.productType === 'auto_brick') {
        resolvedProductType = '__merged_brick__';
      } else if (layer.productType === 'auto_gasblock_part') {
        resolvedProductType = '__merged_gasblock_part__';
      } else if (layer.productType === 'auto_gasblock_wall') {
        resolvedProductType = '__merged_gasblock_wall__';
      }
    }

    // Отримуємо продукти
    let products;
    if (resolvedProductType === '__merged_uw__') {
      products = [
        ...getProductsByType('profile_uw_50'),
        ...getProductsByType('profile_uw_75'),
        ...getProductsByType('profile_uw_100'),
      ];
    } else if (resolvedProductType === '__merged_gkl__') {
      products = [
        ...getProductsByType('gkl_standard'),
        ...getProductsByType('gkl_moisture'),
        ...getProductsByType('gkl_fire'),
        ...getProductsByType('gkl_fire_moist'),
      ];
    } else if (resolvedProductType === '__merged_insulation__') {
      const ptW = dimensions.ptWidth || '75';
      products = [
        ...getProductsByType('insulation_50'),
        ...(parseInt(ptW) >= 75 ? getProductsByType('insulation_75') : []),
        ...(parseInt(ptW) >= 100 ? getProductsByType('insulation_100') : []),
      ];
    } else if (resolvedProductType === '__merged_board__') {
      products = [...getProductsByType('osb_board'), ...getProductsByType('plywood_board')];
    } else if (resolvedProductType === '__merged_shelf_guide__') {
      products = [
        ...getProductsByType('profile_ud_27'),
        ...getProductsByType('profile_uw_50'), ...getProductsByType('profile_uw_75'), ...getProductsByType('profile_uw_100'),
      ];
    } else if (resolvedProductType === '__merged_shelf_stud__') {
      products = [
        ...getProductsByType('profile_cd_60'),
        ...getProductsByType('profile_cw_50'), ...getProductsByType('profile_cw_75'), ...getProductsByType('profile_cw_100'),
      ];
    } else if (resolvedProductType === '__merged_brick__') {
      products = [...getProductsByType('msn_brick')];
    } else if (resolvedProductType === '__merged_gasblock_part__') {
      products = [...getProductsByType('gasblock_100'), ...getProductsByType('gasblock_150')];
    } else if (resolvedProductType === '__merged_gasblock_wall__') {
      products = [...getProductsByType('gasblock_200'), ...getProductsByType('gasblock_300'), ...getProductsByType('gasblock_375'), ...getProductsByType('gasblock_400')];
    } else if (resolvedProductType === '__merged_tile_adhesive_all__') {
      products = [
        ...getProductsByType('tile_adhesive'),
        ...getProductsByType('tile_adhesive_elastic'),
        ...getProductsByType('tile_adhesive_white'),
      ];
    } else if (resolvedProductType === '__merged_tile_adhesive_warm__') {
      products = getProductsByType('tile_adhesive_elastic');
    } else {
      products = getProductsByType(resolvedProductType);
    }

    const selectedProductId = productSelections[layer.id];
    let product = null;
    if (selectedProductId) product = products.find(p => p.id === selectedProductId);
    if (!product && layer.defaultProduct) product = products.find(p => p.id === layer.defaultProduct);
    if (!product) product = products[0] || null;
    const thickness = layer.hasThickness ? (parseFloat(layerThicknesses[layer.id]) || layer.defaultThickness || 50) : null;
    let qty = 0;
    if (product) {
      // Для шарів стяжки в технологіях з водяною ТП — коригуємо розрахунок
      if (hasWarmWater && isScreedLayer(layer) && thickness && product.consumptionPer10mm) {
        // Верхня зона: повна площа × (товщина - діаметр труби)
        const upperThickness = Math.max(thickness - pipeDiameter, 0);
        const upperQty = product.consumptionPer10mm * (upperThickness / 10) * area;
        // Нижня зона: зменшена площа × діаметр труби
        const lowerArea = area - pipeAreaInSection;
        const lowerQty = product.consumptionPer10mm * (pipeDiameter / 10) * Math.max(lowerArea, 0);
        qty = upperQty + lowerQty;
      } else if (product.consumptionPer10mm && thickness) {
        qty = product.consumptionPer10mm * (thickness / 10) * area;
      } else if (layer.calcType === 'area' && product.consumption) {
        qty = area * product.consumption;
      } else if (layer.calcType === 'perimeter' && product.consumption) {
        qty = perimeter * product.consumption;
      } else if (layer.calcType === 'special') {
        qty = beaconCount;
      } else if (layer.calcType === 'special_drywall') {
        // ГКЛ: горизонтальний шов (якщо висота > 2м) + вертикальні шви
        const h = parseFloat(dimensions.height) || 2.7;
        const wl = parseFloat(dimensions.wallLength) || 0;
        const horizontalSeam = h > 2 ? wl : 0;
        const verticalSeams = Math.floor(wl / 1.2) * h;
        const totalSeams = horizontalSeam + verticalSeams;
        qty = totalSeams * (product?.consumption || 1);
      }
      // === PARTITION calc types (з Knauf PDF) ===
      else if (layer.calcType === 'partition_uw') {
        // UW: підлога + стеля = довжина перегородки × 2
        const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
        qty = pl * 2;
      } else if (layer.calcType === 'partition_uw_double') {
        // Подвійний каркас: × 2 рейки × 2 (підлога + стеля)
        const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
        qty = pl * 4;
      } else if (layer.calcType === 'partition_cw') {
        // CW: кількість стійок × висота × 2 сторони? Ні — стійки спільні для обох сторін
        const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 2.7;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.floor(pl / step) + 1;
        qty = studs * h;
      } else if (layer.calcType === 'partition_cw_double') {
        // Подвійний каркас: × 2
        const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 2.7;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.floor(pl / step) + 1;
        qty = studs * h * 2;
      } else if (layer.calcType === 'partition_gkl') {
        const gklLayers = layer.gklLayers || 1;
        const sides = 2;
        qty = area * gklLayers * sides;
        // Якщо OSB/фанера увімкнена — мінус 1 шар з кожного боку
        const boardLayer = technology.layers?.find(l => l.id === 'board');
        const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
        if (boardEnabled && gklLayers >= 2) {
          qty = area * (gklLayers - 1) * sides;
        }
      } else if (layer.calcType === 'partition_putty') {
        // Фуген: залежить від кількості шарів (з Knauf PDF)
        const gklL = technology.layers?.find(l => l.id === 'gkl')?.gklLayers || 1;
        const puttyRate = gklL === 1 ? 0.6 : gklL === 2 ? 1.0 : 1.4;
        qty = area * puttyRate;
      } else if (layer.calcType === 'partition_tape') {
        const gklL = technology.layers?.find(l => l.id === 'gkl')?.gklLayers || 1;
        const tapeRate = gklL === 1 ? 1.5 : 2.0;
        qty = area * tapeRate;
      } else if (layer.calcType === 'partition_insulation') {
        // Вата: 1 м²/м² площі перегородки
        qty = area * (product?.consumption || 1.0);
      } else if (layer.calcType === 'partition_board') {
        // OSB/фанера: 2 сторони
        qty = area * 2;
      }
      // === CLADDING calc types ===
      else if (layer.calcType === 'cladding_ud') {
        // UD-27: підлога + стеля = 2 × довжина стіни
        const wl = parseFloat(dimensions.wallLength) || 0;
        qty = wl * 2;
      } else if (layer.calcType === 'cladding_cd') {
        // CD-60: кількість стійок × висота
        const wl = parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 2.7;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.floor(wl / step) + 1;
        qty = studs * h;
      } else if (layer.calcType === 'cladding_hanger') {
        // Підвіси: на кожну стійку CD, крок по висоті ~800мм
        const wl = parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 2.7;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.floor(wl / step) + 1;
        const hangersPerStud = Math.ceil(h / 0.8);
        qty = studs * hangersPerStud;
      } else if (layer.calcType === 'cladding_gkl') {
        // ГКЛ: 1 сторона × кількість шарів
        const gklLayers = layer.gklLayers || 1;
        qty = area * gklLayers;
        // Якщо OSB/фанера увімкнена — мінус 1 шар
        const boardLayer = technology.layers?.find(l => l.id === 'board');
        const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
        if (boardEnabled && gklLayers >= 2) {
          qty = area * (gklLayers - 1);
        }
      } else if (layer.calcType === 'cladding_uw') {
        // W625: UW по підлозі + стелі
        const wl = parseFloat(dimensions.wallLength) || 0;
        qty = wl * 2;
      } else if (layer.calcType === 'cladding_cw') {
        // W625: CW стійки, одна сторона
        const wl = parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 2.7;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.floor(wl / step) + 1;
        qty = studs * h;
      } else if (layer.calcType === 'cladding_perlfix_direct') {
        // W611a: Perlfix безпосередньо 3.5 кг/м²
        qty = area * 3.5;
      } else if (layer.calcType === 'cladding_perlfix_strips') {
        // W611b: Perlfix на маяки 4.5 кг/м²
        qty = area * 4.5;
      } else if (layer.calcType === 'cladding_insulation') {
        // Вата: 1 м²/м²
        qty = area * (product?.consumption || 1.0);
      } else if (layer.calcType === 'cladding_board') {
        // OSB/фанера: 1 сторона
        qty = area;
      }
      // === GKL CONSTRUCT calc types (universal, planes-based) ===
      else if (layer.calcType === 'gc_ud') {
        const planes = dimensions._gcPlanes || [];
        const techTags = dimensions._gcTechTags || [];
        let udTotal = 0;
        planes.forEach(p => { udTotal += (p.w + p.h) * 2; });
        if (techTags.includes('gc_install')) udTotal += 2.7;
        qty = udTotal;
      } else if (layer.calcType === 'gc_cd') {
        const planes = dimensions._gcPlanes || [];
        const techTags = dimensions._gcTechTags || [];
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const depth = dimensions._gcDepth || 0;
        let cdTotal = 0;
        planes.forEach(p => {
          if (p.w >= 0.5) {
            const studs = 1 + Math.floor(Math.max(p.w - 0.5, 0) / step);
            cdTotal += studs * p.h;
          } else if (p.w > 0) {
            const bars = Math.max(Math.floor(p.h / 0.6) - 1, 0);
            cdTotal += bars * p.w;
          }
        });
        // Г-короб горизонтальний: CD-кронштейни якщо depth > 0.13
        if (techTags.includes('gc_box_h') && depth > 0.13) {
          const wallPlane = planes.find(p => p.type === 'wall');
          if (wallPlane) {
            const wallStuds = Math.floor(wallPlane.w / step) + 1;
            const hangersPerStud = Math.ceil(wallPlane.h / 0.8);
            const bracketCount = wallStuds * hangersPerStud;
            cdTotal += bracketCount * depth * 1.1;
          }
        }
        qty = cdTotal;
      } else if (layer.calcType === 'gc_hanger') {
        const planes = dimensions._gcPlanes || [];
        const depth = dimensions._gcDepth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        if (depth <= 0.13) {
          const wallPlane = planes.find(p => p.type === 'wall');
          if (wallPlane) {
            const studs = Math.floor(wallPlane.w / step) + 1;
            const hangersPerStud = Math.ceil(wallPlane.h / 0.8);
            qty = studs * hangersPerStud;
          }
        } else {
          qty = 0;
        }
      } else if (layer.calcType === 'gc_gkl') {
        const gklLayers = layer.gklLayers || 1;
        qty = area * gklLayers;
        const boardLayer = technology.layers?.find(l => l.id === 'board');
        const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
        if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1);
      } else if (layer.calcType === 'gc_insulation') {
        qty = area * (product?.consumption || 1.0);
      } else if (layer.calcType === 'gc_board') {
        qty = area;
      } else if (layer.calcType === 'gc_shelf_guide') {
        const sLen = dimensions._gcShelfLen || 0;
        const sDep = dimensions._gcShelfDepth || 0;
        const sCount = dimensions._gcShelfCount || 1;
        qty = (sLen + sDep) * 2 * sCount;
      } else if (layer.calcType === 'gc_shelf_stud') {
        const sLen = dimensions._gcShelfLen || 0;
        const sDep = dimensions._gcShelfDepth || 0;
        const sCount = dimensions._gcShelfCount || 1;
        const step = parseFloat(dimensions.profileStep) || 0.6;
        const studs = Math.max(Math.floor(sDep / step) - 1, 0);
        qty = studs * sLen * sCount;
      } else if (layer.calcType === 'gc_shelf_gkl') {
        const sLen = dimensions._gcShelfLen || 0;
        const sDep = dimensions._gcShelfDepth || 0;
        const sCount = dimensions._gcShelfCount || 1;
        const gklLayers = layer.gklLayers || 1;
        const gklThick = 0.0125;
        let profileW = 0.027;
        const guideProduct = productSelections['shelf_guide'] || '';
        if (guideProduct.includes('uw50') || guideProduct.includes('cw50')) profileW = 0.05;
        else if (guideProduct.includes('uw75') || guideProduct.includes('cw75')) profileW = 0.075;
        else if (guideProduct.includes('uw100') || guideProduct.includes('cw100')) profileW = 0.1;
        const topBottom = sLen * sDep * 2;
        const endcap = sLen * (profileW + gklThick * gklLayers * 2);
        qty = (topBottom + endcap) * sCount * gklLayers;
      }
      // === CEILING calc types ===
      else if (layer.calcType === 'ceil_ud') {
        // UD по периметру кімнати
        qty = ((dimensions._ceilLength || 0) + (dimensions._ceilWidth || 0)) * 2;
      } else if (layer.calcType === 'ceil_cd_single') {
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const drop = dimensions._ceilDrop || 0.1;
        const rows = Math.floor(W / step) + 1;
        qty = rows * L;
        // Кронштейни з CD якщо drop > 130мм
        if (drop > 0.13) {
          const hangersPerRow = Math.ceil(L / 0.8);
          const brackets = rows * hangersPerRow;
          qty += brackets * drop * 1.1;
        }
      } else if (layer.calcType === 'ceil_cd_double') {
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const drop = dimensions._ceilDrop || 0.1;
        const mainRows = Math.floor(W / 1.2) + 1;
        const mainLm = mainRows * L;
        const bearingRows = Math.floor(L / step) + 1;
        const bearingLm = bearingRows * W;
        qty = mainLm + bearingLm;
        if (drop > 0.13) {
          const hangersPerRow = Math.ceil(L / 0.8);
          const brackets = mainRows * hangersPerRow;
          qty += brackets * drop * 1.1;
        }
      } else if (layer.calcType === 'ceil_crab') {
        // Краби: на кожному перетині основних × несучих
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const mainRows = Math.floor(W / 1.2) + 1;
        const bearingRows = Math.floor(L / step) + 1;
        qty = mainRows * bearingRows;
      } else if (layer.calcType === 'ceil_hanger_single') {
        // D112: підвіси на кожен ряд CD, крок 800мм
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const drop = dimensions._ceilDrop || 0.1;
        const rows = Math.floor(W / step) + 1;
        if (drop <= 0.13) {
          const hangersPerRow = Math.ceil(L / 0.8);
          qty = rows * hangersPerRow;
        } else {
          qty = 0; // кронштейни з CD — додаються до ceil_cd_single
        }
      } else if (layer.calcType === 'ceil_hanger_double') {
        // D113: підвіси тільки до основних CD
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const drop = dimensions._ceilDrop || 0.1;
        const mainRows = Math.floor(W / 1.2) + 1;
        if (drop <= 0.13) {
          const hangersPerRow = Math.ceil(L / 0.8);
          qty = mainRows * hangersPerRow;
        } else {
          qty = 0;
        }
      } else if (layer.calcType === 'ceil_extender_single') {
        // D112: подовжувачі для кожного ряду CD де довжина > 3м
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const rows = Math.floor(W / step) + 1;
        const jointsPerRow = Math.max(Math.ceil(L / 3) - 1, 0);
        qty = rows * jointsPerRow;
      } else if (layer.calcType === 'ceil_extender_double') {
        // D113: подовжувачі для основних + несучих
        const L = dimensions._ceilLength || 0;
        const W = dimensions._ceilWidth || 0;
        const step = parseFloat(dimensions.profileStep) || 0.4;
        const mainRows = Math.floor(W / 1.2) + 1;
        const mainJoints = mainRows * Math.max(Math.ceil(L / 3) - 1, 0);
        const bearingRows = Math.floor(L / step) + 1;
        const bearingJoints = bearingRows * Math.max(Math.ceil(W / 3) - 1, 0);
        qty = mainJoints + bearingJoints;
      } else if (layer.calcType === 'ceil_gkl') {
        const gklLayers = layer.gklLayers || 1;
        qty = area * gklLayers;
        const boardLayer = technology.layers?.find(l => l.id === 'board');
        const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
        if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1);
      } else if (layer.calcType === 'ceil_insulation') {
        qty = area * (product?.consumption || 1.0);
      } else if (layer.calcType === 'ceil_board') {
        qty = area;
      }
      // === MASONRY calc types ===
      // Газоблок: розмір визначається з обраного product_type (gasblock_100..400)
      else if (layer.calcType === 'msn_gas_block') {
        // Визначаємо розмір з обраного продукту
        const pid = product?.id || '';
        const is375 = pid.includes('375');
        const rowH = is375 ? 0.25 : 0.2; // висота блоку
        const pcsPerM2 = 1 / (rowH * 0.6); // 1/(h×0.6)
        qty = area * pcsPerM2 * 1.05; // +5% запас
        // Зберігаємо товщину для інших формул
        let thk = 0.2;
        if (pid.includes('100')) thk = 0.1;
        else if (pid.includes('150')) thk = 0.15;
        else if (pid.includes('200')) thk = 0.2;
        else if (pid.includes('300')) thk = 0.3;
        else if (pid.includes('375')) thk = 0.375;
        else if (pid.includes('400')) thk = 0.4;
        dimensions._msnBlockThk = thk;
        dimensions._msnBlockH = rowH;
      } else if (layer.calcType === 'msn_gas_glue') {
        // Клей: залежить від товщини блоку (горизонтальний + вертикальний шви)
        // Формула: 1.4 кг/м²/мм × шов 2.5мм × thk × 0.8 × 8.33 блоків/м² ≈ thk × 23.3
        const thk = dimensions._msnBlockThk || 0.2;
        qty = area * thk * 23.3;
      } else if (layer.calcType === 'msn_mortar_first') {
        // ЦПР 1-й ряд готова суміш: довжина × товщина × 12мм шов × 1800 кг/м³
        const wl = parseFloat(dimensions.wallLength) || 0;
        const thk = dimensions._msnBlockThk || 0.2;
        qty = wl * thk * 0.012 * 1800;
      } else if (layer.calcType === 'msn_cement_first') {
        // Цемент 1-й ряд: об'єм × 300 кг/м³
        const wl = parseFloat(dimensions.wallLength) || 0;
        const thk = dimensions._msnBlockThk || 0.2;
        qty = wl * thk * 0.012 * 300;
      } else if (layer.calcType === 'msn_sand_first') {
        const wl = parseFloat(dimensions.wallLength) || 0;
        const thk = dimensions._msnBlockThk || 0.2;
        qty = wl * thk * 0.012 * 1600 * 0.8;
      } else if (layer.calcType === 'msn_plasti_first') {
        const wl = parseFloat(dimensions.wallLength) || 0;
        const thk = dimensions._msnBlockThk || 0.2;
        qty = wl * thk * 0.012 * 300 * 0.005;
      } else if (layer.calcType === 'msn_hydro') {
        const wl = parseFloat(dimensions.wallLength) || 0;
        const thk = dimensions._msnBlockThk || 0.2;
        qty = wl * (thk + 0.2);
      } else if (layer.calcType === 'msn_rebar_1row') {
        const wl = parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 0;
        const rebarEvery = parseInt(dimensions.rebarEvery) || 3;
        const rowH = dimensions._msnBlockH || 0.2;
        const totalRows = Math.ceil(h / rowH);
        const rebarRows = Math.max(Math.floor(totalRows / rebarEvery), 0);
        qty = rebarRows * wl * 1;
      } else if (layer.calcType === 'msn_rebar_2row') {
        const wl = parseFloat(dimensions.wallLength) || 0;
        const h = parseFloat(dimensions.height) || 0;
        const rebarEvery = parseInt(dimensions.rebarEvery) || 3;
        const rowH = dimensions._msnBlockH || 0.2;
        const totalRows = Math.ceil(h / rowH);
        const rebarRows = Math.max(Math.floor(totalRows / rebarEvery), 0);
        qty = rebarRows * wl * 2;
      }
      // Цегла: блоки
      else if (layer.calcType === 'msn_brick_half') {
        const isHalf = product?.id?.includes('half');
        qty = area * (isHalf ? 39 : 51) * 1.05;
      } else if (layer.calcType === 'msn_brick_one') {
        const isHalf = product?.id?.includes('half');
        qty = area * (isHalf ? 78 : 102) * 1.05;
      } else if (layer.calcType === 'msn_brick_onehalf') {
        const isHalf = product?.id?.includes('half');
        qty = area * (isHalf ? 117 : 153) * 1.05;
      }
      // Цегла/шлак: ЦПР готова суміш (об'єм × 1800)
      else if (layer.calcType === 'msn_cpr_half')     { qty = area * 0.023 * 1800; }
      else if (layer.calcType === 'msn_cpr_one')      { qty = area * 0.055 * 1800; }
      else if (layer.calcType === 'msn_cpr_onehalf')  { qty = area * 0.089 * 1800; }
      else if (layer.calcType === 'msn_cpr_slag90')   { qty = area * 0.016 * 1800; }
      else if (layer.calcType === 'msn_cpr_slag190')  { qty = area * 0.028 * 1800; }
      // Цегла/шлак: цемент DIY (об'єм × 300)
      else if (layer.calcType === 'msn_cem_half')     { qty = area * 0.023 * 300; }
      else if (layer.calcType === 'msn_cem_one')      { qty = area * 0.055 * 300; }
      else if (layer.calcType === 'msn_cem_onehalf')  { qty = area * 0.089 * 300; }
      else if (layer.calcType === 'msn_cem_slag90')   { qty = area * 0.016 * 300; }
      else if (layer.calcType === 'msn_cem_slag190')  { qty = area * 0.028 * 300; }
      // Цегла/шлак: пісок DIY (об'єм × 1600 × 0.8)
      else if (layer.calcType === 'msn_sand_half')    { qty = area * 0.023 * 1600 * 0.8; }
      else if (layer.calcType === 'msn_sand_one')     { qty = area * 0.055 * 1600 * 0.8; }
      else if (layer.calcType === 'msn_sand_onehalf') { qty = area * 0.089 * 1600 * 0.8; }
      else if (layer.calcType === 'msn_sand_slag90')  { qty = area * 0.016 * 1600 * 0.8; }
      else if (layer.calcType === 'msn_sand_slag190') { qty = area * 0.028 * 1600 * 0.8; }
      // Пластифікатор (0.5% від маси цементу)
      else if (layer.calcType === 'msn_plasti_half')    { qty = area * 0.023 * 300 * 0.005; }
      else if (layer.calcType === 'msn_plasti_one')     { qty = area * 0.055 * 300 * 0.005; }
      else if (layer.calcType === 'msn_plasti_onehalf') { qty = area * 0.089 * 300 * 0.005; }
      else if (layer.calcType === 'msn_plasti_slag90')   { qty = area * 0.016 * 300 * 0.005; }
      else if (layer.calcType === 'msn_plasti_slag190')  { qty = area * 0.028 * 300 * 0.005; }
      // Шлакоблок
      else if (layer.calcType === 'msn_slag_90')  { qty = area * 22 * 1.10; }
      else if (layer.calcType === 'msn_slag_190') { qty = area * 13 * 1.10; }
      // Сітка
      else if (layer.calcType === 'msn_mesh') { qty = area * 0.25; }
      // === LINTEL calc types ===
      else if (['msn_lintel_aeroc','msn_lintel_angle_part','msn_lintel_angle_wall',
               'msn_lintel_rebar_part','msn_lintel_rebar_wall','msn_lintel_zb',
               'msn_lintel_angle_brick','msn_lintel_zb_slag','msn_lintel_angle_slag'].includes(layer.calcType)) {
        // Новий формат: масиви doorOpenings/windowOpenings
        const doorsList = Array.isArray(dimensions.doorOpenings) ? dimensions.doorOpenings : [];
        const windowsList = Array.isArray(dimensions.windowOpenings) ? dimensions.windowOpenings : [];
        const doorsCount = doorsList.length || parseInt(dimensions.doors) || 0;
        const windowsCount = windowsList.length || parseInt(dimensions.windows) || 0;
        const totalOpenings = doorsCount + windowsCount;
        // Точна довжина для кожного отвору: (ширина + 0.5м опирання)
        const doorLm = doorsList.length > 0
          ? doorsList.reduce((s, o) => s + (parseFloat(o.w) || 0.9) + 0.5, 0)
          : doorsCount * ((parseFloat(dimensions.doorWidth) || 0.9) + 0.5);
        const winLm = windowsList.length > 0
          ? windowsList.reduce((s, o) => s + (parseFloat(o.w) || 1.5) + 0.5, 0)
          : windowsCount * ((parseFloat(dimensions.windowWidth) || 1.5) + 0.5);

        if (layer.calcType === 'msn_lintel_aeroc') {
          qty = totalOpenings; // 1 шт на отвір
        } else if (layer.calcType === 'msn_lintel_angle_part') {
          qty = doorLm + winLm; // 1 кутник × п.м.
        } else if (layer.calcType === 'msn_lintel_angle_wall') {
          qty = (doorLm + winLm) * 2; // 2 кутники × п.м.
        } else if (layer.calcType === 'msn_lintel_rebar_part') {
          qty = (doorLm + winLm) * 2; // 2 прути × п.м.
        } else if (layer.calcType === 'msn_lintel_rebar_wall') {
          qty = (doorLm + winLm) * 3; // 3 прути × п.м.
        } else if (layer.calcType === 'msn_lintel_zb') {
          const techId = technology.id || '';
          let perOpening = 1;
          if (techId === 'msn_brick_one') perOpening = 2;
          if (techId === 'msn_brick_onehalf') perOpening = 3;
          qty = totalOpenings * perOpening;
        } else if (layer.calcType === 'msn_lintel_angle_brick' || layer.calcType === 'msn_lintel_angle_slag') {
          qty = (doorLm + winLm) * 2;
        } else if (layer.calcType === 'msn_lintel_zb_slag') {
          qty = totalOpenings;
        }
      }
      // === TILING calc types ===
      else if (layer.calcType === 'tiling_adhesive') {
        // Витрата клею залежить від розміру плитки:
        // мозаїка або ≤200мм → 3.0 кг/м²
        // 200–400мм → 4.0 кг/м²
        // 400–600мм → 5.0 кг/м²
        // >600мм → 6.5 кг/м²
        const isMosaic = (dimensions._tilingType || 'standard') === 'mosaic';
        let dynamicConsumption;
        if (isMosaic) {
          dynamicConsumption = 3.0;
        } else {
          const tileW = dimensions._tileW || 0;
          const tileH = dimensions._tileH || 0;
          const maxSide = Math.max(tileW, tileH); // орієнтуємось на більшу сторону
          if (maxSide <= 200) dynamicConsumption = 3.0;
          else if (maxSide <= 400) dynamicConsumption = 4.0;
          else if (maxSide <= 600) dynamicConsumption = 5.0;
          else dynamicConsumption = 6.5;
        }
        qty = area * dynamicConsumption;
      } else if (layer.calcType === 'tiling_waterproof') {
        // Гідроізоляція: окрема площа введена користувачем
        const hydroArea = dimensions._hydroArea || 0;
        qty = hydroArea * (product.consumption || 1.5);
      } else if (layer.calcType === 'tiling_waterproof_tape') {
        // Стрічка: зовнішні + внутрішні кути п.м.
        const outerC = dimensions._outerCorners || 0;
        const innerC = dimensions._innerCorners || 0;
        qty = (outerC + innerC) * (product.consumption || 1.0);
      }
    }
    const packaging = product?.packaging || 1;

    // Штроби — додаємо об'єм до стартової шпаклівки
    if ((workType === 'wall_prep' || workType === 'ceiling_prep') && layer.id === 'start' && dimensions.hasGrooves && parseFloat(dimensions.grooveLength) > 0) {
      const gl = parseFloat(dimensions.grooveLength);
      // Переріз 1.5×1.5 см = 0.015×0.015 м, густота шпаклівки ~1300 кг/м³
      const grooveKg = gl * 0.015 * 0.015 * 1300;
      qty += grooveKg;
    }

    // coats: множник шарів (грунтовка 4 шари, тощо)
    if (layer.coats && layer.coats > 1) {
      qty = qty * layer.coats;
    }

    return {
      ...layer, qty: Math.ceil(qty * 100) / 100, qtyRounded: Math.ceil(qty),
      product, products, packs: Math.ceil(qty / packaging),
      packagingUnit: product?.packagingUnit || '', packagingSize: packaging,
      unit: product?.unit || 'шт.', thickness, isEnabled,
      factQty: Math.round(qty * 100) / 100,
      factUnit: product?.unit || null,
      productId: product?.id || null,
    };
  });

  const calculatedConsumables = (technology.consumables || []).map(item => {
    // ── Перевірка condition ──
    if (item.condition) {
      const c = item.condition;
      const techTags = technology.tags || [];
      if (c.tag && !techTags.includes(c.tag)) return { ...item, qty: 0 };
      if (c.tag_not && techTags.includes(c.tag_not)) return { ...item, qty: 0 };
      if (c.gkl_layers !== undefined) {
        const gklLayer = technology.layers?.find(l => l.id === 'gkl');
        const actualLayers = gklLayer?.gklLayers || 1;
        if (actualLayers !== c.gkl_layers) return { ...item, qty: 0 };
      }
      if (c.gkl_layers_gte !== undefined) {
        const gklLayer = technology.layers?.find(l => l.id === 'gkl');
        const actualLayers = gklLayer?.gklLayers || 1;
        if (actualLayers < c.gkl_layers_gte) return { ...item, qty: 0 };
      }
      if (c.param) {
        const paramValue = dimensions[c.param] || dimensions['_' + c.param];
        if (c.value !== undefined && paramValue !== c.value) return { ...item, qty: 0 };
        if (c.value_gt !== undefined && !(paramValue > c.value_gt)) return { ...item, qty: 0 };
      }
      if (c.product_id) {
        const matchingLayer = calculatedLayers.find(l => l.product && l.product.id === c.product_id && l.isEnabled !== false);
        if (!matchingLayer) return { ...item, qty: 0 };
      }
      if (c.has_rebar) {
        const rebarLayer = calculatedLayers.find(l => (l.calcType === 'msn_rebar_1row' || l.calcType === 'msn_rebar_2row') && l.isEnabled !== false);
        if (!rebarLayer || rebarLayer.qtyRounded <= 0) return { ...item, qty: 0 };
      }
    }
    // ── Розрахунок qty ──
    let qty = 0;
    if (item.calcType === 'area' && item.consumption) qty = Math.ceil(area * item.consumption);
    else if (item.calcType === 'perimeter' && item.consumption) qty = Math.ceil(perimeter * item.consumption);
    else if (item.calcType === 'fixed') qty = item.qty || 0;
    else if (item.calcType === 'formula' && item.formula) {
      qty = evalFormula(item.formula, area, perimeter, {
        outerCorners: dimensions._outerCorners || 0,
        innerCorners: dimensions._innerCorners || 0,
        height: dimensions._height || 0,
        openingsArea: dimensions._openingsArea || 0,
        doors: dimensions._doors || dimensions._ptDoors || 0,
        doorW: dimensions._doorW || dimensions._ptDoorW || 0.9,
        windows: dimensions._windows || 0,
        windowsLm: dimensions._windowsLm || 0,
        outerCornersTotalLm: dimensions._outerCornersTotalLm || 0,
        nichesLength: dimensions._nichesLength || 0,
        ceilPerimeter: dimensions._ceilPerimeter || 0,
        ceilLength: dimensions._ceilLength || 0,
        ceilWidth: dimensions._ceilWidth || 0,
        ptLength: dimensions._ptLength || 0,
        tilesPerM2: (dimensions._tileW > 0 && dimensions._tileH > 0)
          ? (1000 / (dimensions._tileW + (dimensions._groutWidth || 2))) * (1000 / (dimensions._tileH + (dimensions._groutWidth || 2)))
          : 0,
        rebarQty: (calculatedLayers.find(l => (l.calcType === 'msn_rebar_1row' || l.calcType === 'msn_rebar_2row') && l.isEnabled !== false) || {}).qtyRounded || 0,
        totalOpenings: (dimensions._doors || 0) + (dimensions._windows || 0),
        hangerQty: (calculatedLayers.find(l => l.id === 'hanger') || {}).qtyRounded || 0,
        udTotal: (() => {
          const planes = dimensions._gcPlanes || [];
          let ud = planes.reduce((s, p) => s + (p.w + p.h) * 2, 0);
          if ((dimensions._gcTechTags || []).includes('gc_install')) ud += 2.7;
          if ((dimensions._gcTechTags || []).includes('gc_shelf')) {
            ud = ((dimensions._gcShelfLen || 0) + (dimensions._gcShelfDepth || 0)) * 2 * (dimensions._gcShelfCount || 1);
          }
          return ud;
        })(),
      });
    }
    else if (item.calcType === 'special') qty = beaconCount;
    return { ...item, qty };
  }).filter(c => c.qty > 0);

  // Динамічні consumables тепер в базі (calc_technologies.consumables з condition)

  return {
    layers: calculatedLayers, consumables: calculatedConsumables,
    tools: technology.tools || [],
    area: Math.round(area * 100) / 100, perimeter: Math.round(perimeter * 100) / 100,
    pipeLength, workType,
  };
}

export function generateAIPrompt(technologies, workType) {
  const workNames = {
    'screed': 'стяжка підлоги',
    'tiling': 'укладка плитки',
    'wall_prep': 'малярні роботи (шпаклювання стін/стелі)',
    'ceiling_prep': 'підготовка стелі',
    'plaster': 'штукатурка стін',
    'cladding': 'облицювання стін гіпсокартоном',
    'partition': 'перегородки ГКЛ',
    'gkl_construct': 'конструкції з ГКЛ',
    'masonry': 'мурування стін',
    'ceiling': 'підвісні стелі ГКЛ',
  };
  const workName = workNames[workType] || 'будівельні роботи';
  const techList = technologies.map(t =>
    `- ${t.id}: "${t.name}" [${t.brand}] — теги: ${t.tags.join(', ')}`
  ).join('\n');
  return `Ти — AI-помічник будівельного калькулятора LIHTAR.
Користувач описує задачу по типу робіт: ${workName}.
Твоя задача — порекомендувати 1-3 найкращі технології зі списку нижче.

Доступні технології:
${techList}

ПРАВИЛА (СУВОРО):
1. Якщо можеш рекомендувати — відповідай ТІЛЬКИ JSON масивом без жодного тексту до чи після: [{"techId":"...","reason":"..."}]
2. Максимум 3 варіанти, сортуй від найкращого до найгіршого
3. reason — 1 речення українською, пояснює чому саме ця технологія
4. Якщо недостатньо інформації — відповідай ТІЛЬКИ текстом з одним уточнюючим питанням
5. НІКОЛИ не мішай JSON і текст в одній відповіді`;
}

// Промт для підбору тайпів робіт на головному екрані
export function generateWorkTypePrompt() {
  // Точно відповідає WORK_TYPES з available:true
  const workTypes = [
    { id: 'screed',        name: 'Стяжка підлоги',          desc: 'вирівнювання підлоги цементною або напівсухою стяжкою' },
    { id: 'plaster',       name: 'Штукатурка стін',          desc: 'перший грубий шар по маяках, НЕ шпаклювання' },
    { id: 'wall_prep',     name: 'Малярні роботи',           desc: 'шпаклювання + шліфування стін і стелі під фарбу або шпалери' },
    { id: 'partition',     name: 'Перегородки ГКЛ',          desc: 'нові перегородки з гіпсокартону на металевому каркасі' },
    { id: 'cladding',      name: 'Облицювання стін ГКЛ',     desc: 'обшивка існуючих стін гіпсокартоном' },
    { id: 'gkl_construct', name: 'Конструкції з ГКЛ',        desc: 'короби, ніші, полиці, складні форми з гіпсокартону' },
    { id: 'ceiling',       name: 'Підвісні стелі ГКЛ',       desc: 'підвісна стеля з гіпсокартону на профілях' },
    { id: 'masonry',       name: 'Мурування стін',           desc: 'кладка стін або перегородок з цегли, газоблоку, піноблоку' },
    { id: 'tiling',        name: 'Плиточні роботи',          desc: 'укладка керамічної плитки, керамограніту, мозаїки' },
  ];
  const list = workTypes.map(w => `- ${w.id}: "${w.name}" — ${w.desc}`).join('\n');
  return `Ти — AI-помічник будівельного калькулятора LIHTAR.
Юзер описує будівельне завдання. Визнач які розрахунки матеріалів потрібні.

ВАЖЛИВО — кожен вид це окремий розрахунок:
- Штукатурка (plaster) і шпаклювання (wall_prep) — РІЗНІ розрахунки. Якщо спочатку штукатурять а потім шпаклюють — потрібні обидва
- Мурування (masonry) і штукатурка (plaster) — РІЗНІ. Якщо кладуть стіну і штукатурять — обидва
- Малярні роботи (wall_prep) охоплює і стіни і стелю — окремого ceiling_prep немає
- Перегородки (partition) — тільки ГКЛ каркас. Цегляні перегородки — masonry

Доступні види:
${list}

ПРАВИЛА (СУВОРО):
1. Якщо розумієш задачу — відповідай ТІЛЬКИ JSON масивом: [{"workId":"...","reason":"..."}]
2. Максимум 3 види, від найголовнішого
3. reason — 1 коротке речення чому цей розрахунок потрібен
4. Якщо незрозуміло — відповідай ТІЛЬКИ одним уточнюючим питанням
5. НІКОЛИ не мішай JSON і текст в одній відповіді`;
}

// Промт для заповнення форми параметрів
export function generateFillFormPrompt(workType, currentDims) {
  const workNames = {
    'screed':        'стяжка підлоги',
    'tiling':        'укладка плитки',
    'wall_prep':     'малярні роботи (шпаклювання стін/стелі)',
    'ceiling_prep':  'підготовка стелі',
    'plaster':       'штукатурка стін',
    'cladding':      'облицювання стін гіпсокартоном',
    'partition':     'перегородки ГКЛ',
    'gkl_construct': 'конструкції з ГКЛ',
    'masonry':       'мурування стін',
    'ceiling':       'підвісні стелі ГКЛ',
  };

  const fieldDocs = {
    'screed': `Поля:
- length (м) — довжина кімнати. Приклад: "кімната 5 на 4" → length:5, width:4
- width (м) — ширина кімнати
- doors — кількість дверних отворів (число)
- windows — кількість віконних отворів (число)`,

    'tiling': `Поля:
- area (м²) — площа укладки. Приклад: "підлога 20 кв.м" → area:20; "стіна 3х2.7" → area:8.1
- tileWidth (мм) — ширина плитки. Приклад: "плитка 600х600" → tileWidth:600, tileHeight:600
- tileHeight (мм) — висота/довжина плитки
- groutWidth (мм) — ширина шва. Приклад: "шов 2мм" → groutWidth:2`,

    'wall_prep': `Поля:
- height (м) — висота стін. Приклад: "стеля 2.7" → height:2.7
- wallLength (м) — периметр стін (сума довжин всіх стін). Приклад: "кімната 4х5" → wallLength:18 (периметр); "одна стіна 3м" → wallLength:3
- doors — кількість дверей
- windows — кількість вікон
- innerCorners — кількість внутрішніх кутів (де стіни з'єднуються всередині кімнати)
- outerCorners — кількість зовнішніх кутів (виступи, колони)
- hasGrooves (true/false) — чи є штроби під проводку
- grooveLength (м) — довжина штроб. Приклад: "штроби 20м" → hasGrooves:true, grooveLength:20`,

    'plaster': `Поля:
- height (м) — висота стін
- wallLength (м) — периметр стін (сума довжин всіх стін). Приклад: "кімната 4х5" → wallLength:18
- doors — кількість дверей
- windows — кількість вікон
- innerCorners — внутрішні кути
- outerCorners — зовнішні кути`,

    'ceiling_prep': `Поля:
- ceilArea (м²) — площа стелі
- ceilPerimeter (м) — периметр стелі
- hasGrooves (true/false) — чи є штроби
- grooveLength (м) — довжина штроб`,

    'cladding': `Поля:
- height (м) — висота стін
- wallLength (м) — довжина стін що обшиваються
- doors — кількість дверей
- windows — кількість вікон`,

    'partition': `Поля:
- partitionLength (м) — довжина перегородки (більше число). "Стіна 7х3" → partitionLength:7
- height (м) — висота перегородки (зазвичай 2.5-3.5м). ВАЖЛИВО: якщо два числа і незрозуміло що висота — НЕ заповнюй, запитай: "Ви вказали [АхБ] — яке з цих чисел висота перегородки?"
- doorOpenings — масив дверей [{w,h}]. "Двоє дверей 1х2" → [{"w":1,"h":2},{"w":1,"h":2}]
- ptWidth (мм) — профіль: 75 або 100
Приклад: "стіна 7х3, двоє дверей 1х2" → {"partitionLength":7,"height":3,"doorOpenings":[{"w":1,"h":2},{"w":1,"h":2}]}`,

    'gkl_construct': `Поля:
- length (м) — довжина конструкції
- width (м) — ширина
- height (м) — висота`,

    'masonry': `Поля:
- height (м) — висота кладки
- wallLength (м) — довжина стін
- doors — кількість дверей
- windows — кількість вікон`,

    'ceiling': `Поля:
- length (м) — довжина приміщення
- width (м) — ширина приміщення
- ceilDrop (мм) — опускання стелі в мм. Приклад: "опускання 10см" → ceilDrop:100`,
  };

  const workName = workNames[workType] || 'будівельні роботи';
  const fields = fieldDocs[workType] || 'length, width, height';
  const dimsStr = JSON.stringify(currentDims, null, 2);

  return `Ти — AI-помічник будівельного калькулятора LIHTAR.
Юзер заповнює параметри для розрахунку: ${workName}.

Поточні значення полів (порожні = ще не заповнені):
${dimsStr}

${fields}

ПРАВИЛА (СУВОРО):
1. Якщо юзер вказує параметри — відповідай ТІЛЬКИ JSON об'єктом: {"поле": значення, ...}
   Числові значення — без лапок. Boolean — true/false без лапок. Рядки — з лапками.
   Приклад: {"height": 2.7, "wallLength": 18, "hasGrooves": true, "grooveLength": 20}
2. Заповнюй ТІЛЬКИ поля які юзер явно вказав — не вигадуй решту
3. Якщо даних недостатньо — відповідай ТІЛЬКИ одним уточнюючим питанням
4. НІКОЛИ не мішай JSON і текст в одній відповіді
5. Відповідай українською`;
}

// Резолвить layer.productType у фактичний масив продуктів (для auto_* / __merged_*__)
// Використовується і всередині calculateMaterials, і в Dr. LIHTAR.
export function resolveLayerProducts(layer, workType, dimensions = {}) {
  let resolvedProductType = layer.productType;
  if (workType === 'tiling' && layer.productType === 'tile_adhesive') {
    resolvedProductType = dimensions._tilingWarmFloor ? 'tile_adhesive_elastic' : 'tile_adhesive';
  }
  if (['partition', 'cladding', 'gkl_construct', 'ceiling', 'masonry'].includes(workType)) {
    const ptW = dimensions.ptWidth || '75';
    if (layer.productType === 'auto_uw') resolvedProductType = '__merged_uw__';
    else if (layer.productType === 'auto_cw') resolvedProductType = 'profile_cw_' + ptW;
    else if (layer.productType === 'auto_gkl') resolvedProductType = '__merged_gkl__';
    else if (layer.productType === 'auto_insulation') resolvedProductType = '__merged_insulation__';
    else if (layer.productType === 'auto_board') resolvedProductType = '__merged_board__';
    else if (layer.productType === 'auto_shelf_guide') resolvedProductType = '__merged_shelf_guide__';
    else if (layer.productType === 'auto_shelf_stud') resolvedProductType = '__merged_shelf_stud__';
    else if (layer.productType === 'auto_brick') resolvedProductType = '__merged_brick__';
    else if (layer.productType === 'auto_gasblock_part') resolvedProductType = '__merged_gasblock_part__';
    else if (layer.productType === 'auto_gasblock_wall') resolvedProductType = '__merged_gasblock_wall__';
  }
  const ptW = dimensions.ptWidth || '75';
  if (resolvedProductType === '__merged_uw__') {
    return [
      ...getProductsByType('profile_uw_50'),
      ...getProductsByType('profile_uw_75'),
      ...getProductsByType('profile_uw_100'),
    ];
  }
  if (resolvedProductType === '__merged_gkl__') {
    return [
      ...getProductsByType('gkl_standard'),
      ...getProductsByType('gkl_moisture'),
      ...getProductsByType('gkl_fire'),
      ...getProductsByType('gkl_fire_moist'),
    ];
  }
  if (resolvedProductType === '__merged_insulation__') {
    return [
      ...getProductsByType('insulation_50'),
      ...(parseInt(ptW) >= 75 ? getProductsByType('insulation_75') : []),
      ...(parseInt(ptW) >= 100 ? getProductsByType('insulation_100') : []),
    ];
  }
  if (resolvedProductType === '__merged_board__') {
    return [...getProductsByType('osb_board'), ...getProductsByType('plywood_board')];
  }
  if (resolvedProductType === '__merged_shelf_guide__') {
    return [
      ...getProductsByType('profile_ud_27'),
      ...getProductsByType('profile_uw_50'), ...getProductsByType('profile_uw_75'), ...getProductsByType('profile_uw_100'),
    ];
  }
  if (resolvedProductType === '__merged_shelf_stud__') {
    return [
      ...getProductsByType('profile_cd_60'),
      ...getProductsByType('profile_cw_50'), ...getProductsByType('profile_cw_75'), ...getProductsByType('profile_cw_100'),
    ];
  }
  if (resolvedProductType === '__merged_brick__') return [...getProductsByType('msn_brick')];
  if (resolvedProductType === '__merged_gasblock_part__') {
    return [...getProductsByType('gasblock_100'), ...getProductsByType('gasblock_150')];
  }
  if (resolvedProductType === '__merged_gasblock_wall__') {
    return [...getProductsByType('gasblock_200'), ...getProductsByType('gasblock_300'), ...getProductsByType('gasblock_375'), ...getProductsByType('gasblock_400')];
  }
  if (resolvedProductType === '__merged_tile_adhesive_all__') {
    return [
      ...getProductsByType('tile_adhesive'),
      ...getProductsByType('tile_adhesive_elastic'),
      ...getProductsByType('tile_adhesive_white'),
    ];
  }
  if (resolvedProductType === '__merged_tile_adhesive_warm__') return getProductsByType('tile_adhesive_elastic');
  return getProductsByType(resolvedProductType) || [];
}

// Розрахунок qty для одного ГКЛ layer (partition_*, cladding_*, gc_*, ceil_*)
// Використовується Dr. LIHTAR для інкрементального розрахунку layer'ів без повного циклу calculateMaterials.
// dimensions очікує: area, partitionLength, wallLength, height, profileStep, doors, doorW, ptWidth,
// _gcPlanes, _gcTechTags, _gcDepth, _gcShelfLen, _gcShelfDepth, _gcShelfCount,
// _ceilLength, _ceilWidth, _ceilDrop
export function calcGklLayerQty(layer, product, dimensions, context = {}) {
  const { technology, layerToggles = {}, productSelections = {} } = context;
  const area = parseFloat(dimensions.area) || 0;
  const perimeter = parseFloat(dimensions.perimeter) || 0;
  let qty = 0;

  if (layer.calcType === 'partition_uw') {
    const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
    qty = pl * 2;
  } else if (layer.calcType === 'partition_uw_double') {
    const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
    qty = pl * 4;
  } else if (layer.calcType === 'partition_cw') {
    const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
    const h = parseFloat(dimensions.height) || 2.7;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.floor(pl / step) + 1;
    qty = studs * h;
  } else if (layer.calcType === 'partition_cw_double') {
    const pl = parseFloat(dimensions.partitionLength) || parseFloat(dimensions.wallLength) || 0;
    const h = parseFloat(dimensions.height) || 2.7;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.floor(pl / step) + 1;
    qty = studs * h * 2;
  } else if (layer.calcType === 'partition_gkl') {
    const gklLayers = layer.gklLayers || 1;
    qty = area * gklLayers * 2;
    const boardLayer = technology?.layers?.find(l => l.id === 'board');
    const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
    if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1) * 2;
  } else if (layer.calcType === 'partition_putty') {
    const gklL = technology?.layers?.find(l => l.id === 'gkl')?.gklLayers || 1;
    const puttyRate = gklL === 1 ? 0.6 : gklL === 2 ? 1.0 : 1.4;
    qty = area * puttyRate;
  } else if (layer.calcType === 'partition_tape') {
    const gklL = technology?.layers?.find(l => l.id === 'gkl')?.gklLayers || 1;
    const tapeRate = gklL === 1 ? 1.5 : 2.0;
    qty = area * tapeRate;
  } else if (layer.calcType === 'partition_insulation') {
    qty = area * (product?.consumption || 1.0);
  } else if (layer.calcType === 'partition_board') {
    qty = area * 2;
  }
  // === CLADDING ===
  else if (layer.calcType === 'cladding_ud') {
    const wl = parseFloat(dimensions.wallLength) || 0;
    qty = wl * 2;
  } else if (layer.calcType === 'cladding_cd') {
    const wl = parseFloat(dimensions.wallLength) || 0;
    const h = parseFloat(dimensions.height) || 2.7;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.floor(wl / step) + 1;
    qty = studs * h;
  } else if (layer.calcType === 'cladding_hanger') {
    const wl = parseFloat(dimensions.wallLength) || 0;
    const h = parseFloat(dimensions.height) || 2.7;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.floor(wl / step) + 1;
    const hangersPerStud = Math.ceil(h / 0.8);
    qty = studs * hangersPerStud;
  } else if (layer.calcType === 'cladding_gkl') {
    const gklLayers = layer.gklLayers || 1;
    qty = area * gklLayers;
    const boardLayer = technology?.layers?.find(l => l.id === 'board');
    const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
    if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1);
  } else if (layer.calcType === 'cladding_uw') {
    const wl = parseFloat(dimensions.wallLength) || 0;
    qty = wl * 2;
  } else if (layer.calcType === 'cladding_cw') {
    const wl = parseFloat(dimensions.wallLength) || 0;
    const h = parseFloat(dimensions.height) || 2.7;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.floor(wl / step) + 1;
    qty = studs * h;
  } else if (layer.calcType === 'cladding_perlfix_direct') {
    qty = area * 3.5;
  } else if (layer.calcType === 'cladding_perlfix_strips') {
    qty = area * 4.5;
  } else if (layer.calcType === 'cladding_insulation') {
    qty = area * (product?.consumption || 1.0);
  } else if (layer.calcType === 'cladding_board') {
    qty = area;
  }
  // === GKL CONSTRUCT (gc_*) — з дефолтними planes/tags ===
  else if (layer.calcType === 'gc_ud') {
    const planes = dimensions._gcPlanes || [];
    const techTags = dimensions._gcTechTags || [];
    let udTotal = 0;
    planes.forEach(p => { udTotal += (p.w + p.h) * 2; });
    if (techTags.includes('gc_install')) udTotal += 2.7;
    qty = udTotal;
  } else if (layer.calcType === 'gc_cd') {
    const planes = dimensions._gcPlanes || [];
    const techTags = dimensions._gcTechTags || [];
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const depth = dimensions._gcDepth || 0;
    let cdTotal = 0;
    planes.forEach(p => {
      if (p.w >= 0.5) {
        const studs = 1 + Math.floor(Math.max(p.w - 0.5, 0) / step);
        cdTotal += studs * p.h;
      } else if (p.w > 0) {
        const bars = Math.max(Math.floor(p.h / 0.6) - 1, 0);
        cdTotal += bars * p.w;
      }
    });
    if (techTags.includes('gc_box_h') && depth > 0.13) {
      const wallPlane = planes.find(p => p.type === 'wall');
      if (wallPlane) {
        const wallStuds = Math.floor(wallPlane.w / step) + 1;
        const hangersPerStud = Math.ceil(wallPlane.h / 0.8);
        cdTotal += wallStuds * hangersPerStud * depth * 1.1;
      }
    }
    qty = cdTotal;
  } else if (layer.calcType === 'gc_hanger') {
    const planes = dimensions._gcPlanes || [];
    const depth = dimensions._gcDepth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    if (depth <= 0.13) {
      const wallPlane = planes.find(p => p.type === 'wall');
      if (wallPlane) {
        const studs = Math.floor(wallPlane.w / step) + 1;
        const hangersPerStud = Math.ceil(wallPlane.h / 0.8);
        qty = studs * hangersPerStud;
      }
    }
  } else if (layer.calcType === 'gc_gkl') {
    const gklLayers = layer.gklLayers || 1;
    qty = area * gklLayers;
    const boardLayer = technology?.layers?.find(l => l.id === 'board');
    const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
    if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1);
  } else if (layer.calcType === 'gc_insulation') {
    qty = area * (product?.consumption || 1.0);
  } else if (layer.calcType === 'gc_board') {
    qty = area;
  } else if (layer.calcType === 'gc_shelf_guide') {
    const sLen = dimensions._gcShelfLen || 0;
    const sDep = dimensions._gcShelfDepth || 0;
    const sCount = dimensions._gcShelfCount || 1;
    qty = (sLen + sDep) * 2 * sCount;
  } else if (layer.calcType === 'gc_shelf_stud') {
    const sLen = dimensions._gcShelfLen || 0;
    const sDep = dimensions._gcShelfDepth || 0;
    const sCount = dimensions._gcShelfCount || 1;
    const step = parseFloat(dimensions.profileStep) || 0.6;
    const studs = Math.max(Math.floor(sDep / step) - 1, 0);
    qty = studs * sLen * sCount;
  } else if (layer.calcType === 'gc_shelf_gkl') {
    const sLen = dimensions._gcShelfLen || 0;
    const sDep = dimensions._gcShelfDepth || 0;
    const sCount = dimensions._gcShelfCount || 1;
    const gklLayers = layer.gklLayers || 1;
    const gklThick = 0.0125;
    let profileW = 0.027;
    const guideProduct = productSelections['shelf_guide'] || '';
    if (guideProduct.includes('uw50') || guideProduct.includes('cw50')) profileW = 0.05;
    else if (guideProduct.includes('uw75') || guideProduct.includes('cw75')) profileW = 0.075;
    else if (guideProduct.includes('uw100') || guideProduct.includes('cw100')) profileW = 0.1;
    const topBottom = sLen * sDep * 2;
    const endcap = sLen * (profileW + gklThick * gklLayers * 2);
    qty = (topBottom + endcap) * sCount * gklLayers;
  }
  // === CEILING ===
  else if (layer.calcType === 'ceil_ud') {
    qty = ((dimensions._ceilLength || 0) + (dimensions._ceilWidth || 0)) * 2;
  } else if (layer.calcType === 'ceil_cd_single') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const drop = dimensions._ceilDrop || 0.1;
    const rows = Math.floor(W / step) + 1;
    qty = rows * L;
    if (drop > 0.13) {
      const hangersPerRow = Math.ceil(L / 0.8);
      qty += rows * hangersPerRow * drop * 1.1;
    }
  } else if (layer.calcType === 'ceil_cd_double') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const drop = dimensions._ceilDrop || 0.1;
    const mainRows = Math.floor(W / 1.2) + 1;
    const bearingRows = Math.floor(L / step) + 1;
    qty = mainRows * L + bearingRows * W;
    if (drop > 0.13) {
      const hangersPerRow = Math.ceil(L / 0.8);
      qty += mainRows * hangersPerRow * drop * 1.1;
    }
  } else if (layer.calcType === 'ceil_crab') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const mainRows = Math.floor(W / 1.2) + 1;
    const bearingRows = Math.floor(L / step) + 1;
    qty = mainRows * bearingRows;
  } else if (layer.calcType === 'ceil_hanger_single') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const drop = dimensions._ceilDrop || 0.1;
    const rows = Math.floor(W / step) + 1;
    if (drop <= 0.13) qty = rows * Math.ceil(L / 0.8);
  } else if (layer.calcType === 'ceil_hanger_double') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const drop = dimensions._ceilDrop || 0.1;
    const mainRows = Math.floor(W / 1.2) + 1;
    if (drop <= 0.13) qty = mainRows * Math.ceil(L / 0.8);
  } else if (layer.calcType === 'ceil_extender_single') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const rows = Math.floor(W / step) + 1;
    qty = rows * Math.max(Math.ceil(L / 3) - 1, 0);
  } else if (layer.calcType === 'ceil_extender_double') {
    const L = dimensions._ceilLength || 0;
    const W = dimensions._ceilWidth || 0;
    const step = parseFloat(dimensions.profileStep) || 0.4;
    const mainRows = Math.floor(W / 1.2) + 1;
    const bearingRows = Math.floor(L / step) + 1;
    qty = mainRows * Math.max(Math.ceil(L / 3) - 1, 0) + bearingRows * Math.max(Math.ceil(W / 3) - 1, 0);
  } else if (layer.calcType === 'ceil_gkl') {
    const gklLayers = layer.gklLayers || 1;
    qty = area * gklLayers;
    const boardLayer = technology?.layers?.find(l => l.id === 'board');
    const boardEnabled = boardLayer ? (layerToggles['board'] !== undefined ? layerToggles['board'] : boardLayer.enabled) : false;
    if (boardEnabled && gklLayers >= 2) qty = area * (gklLayers - 1);
  } else if (layer.calcType === 'ceil_insulation') {
    qty = area * (product?.consumption || 1.0);
  } else if (layer.calcType === 'ceil_board') {
    qty = area;
  }
  return qty;
}

// Розрахунок qty для masonry layers (msn_*)
// Dr. LIHTAR MVP: doorOpenings/windowOpenings = [] (юзер вводить чисту площу без отворів)
export function calcMsnLayerQty(layer, product, dimensions) {
  const area = parseFloat(dimensions.area) || 0;
  const ct = layer.calcType || '';
  if (ct === 'msn_gas_block') {
    const pid = product?.id || '';
    const is375 = pid.includes('375');
    const rowH = is375 ? 0.25 : 0.2;
    const pcsPerM2 = 1 / (rowH * 0.6);
    return area * pcsPerM2 * 1.05;
  }
  if (ct === 'msn_gas_glue') {
    return area * (product?.consumption || 5);
  }
  if (ct === 'msn_mortar_first') return area * 0.5; // готовий розчин — кг на перший ряд
  if (ct === 'msn_cement_first') return area * 0.015 * 1400;
  if (ct === 'msn_sand_first') return area * 0.015 * 1600 * 3;
  if (ct === 'msn_plasti_first') return area * 0.02;
  if (ct === 'msn_hydro') return area * (product?.consumption || 1.0);
  if (ct === 'msn_rebar_1row') {
    const wallLength = parseFloat(dimensions.wallLength) || 0;
    return wallLength;
  }
  if (ct === 'msn_rebar_2row') {
    const wallLength = parseFloat(dimensions.wallLength) || 0;
    return wallLength * 2;
  }
  if (ct === 'msn_brick_half') return area * 52;
  if (ct === 'msn_brick_one') return area * 104;
  if (ct === 'msn_brick_onehalf') return area * 156;
  if (ct === 'msn_cpr_half') return area * 0.023 * 1800;
  if (ct === 'msn_cpr_one') return area * 0.055 * 1800;
  return 0;
}

// Розрахунок qty для tiling layers (tiling_adhesive, tiling_waterproof, tiling_waterproof_tape)
// primer використовує area*consumption — проходить через існуючий fallback і не потребує цієї функції.
export function calcTilingLayerQty(layer, product, dimensions) {
  const area = parseFloat(dimensions.area) || 0;
  if (layer.calcType === 'tiling_adhesive') {
    const isMosaic = (dimensions._tilingType || 'standard') === 'mosaic';
    let dynamicConsumption;
    if (isMosaic) {
      dynamicConsumption = 3.0;
    } else {
      const tileW = parseFloat(dimensions._tileW) || 0;
      const tileH = parseFloat(dimensions._tileH) || 0;
      const maxSide = Math.max(tileW, tileH);
      if (maxSide <= 200) dynamicConsumption = 3.0;
      else if (maxSide <= 400) dynamicConsumption = 4.0;
      else if (maxSide <= 600) dynamicConsumption = 5.0;
      else dynamicConsumption = 6.5;
    }
    return area * dynamicConsumption;
  }
  if (layer.calcType === 'tiling_waterproof') {
    const hydroArea = parseFloat(dimensions._hydroArea) || area;
    return hydroArea * (product?.consumption || 1.5);
  }
  if (layer.calcType === 'tiling_waterproof_tape') {
    const outerC = parseFloat(dimensions._outerCorners) || 0;
    const innerC = parseFloat(dimensions._innerCorners) || 0;
    return (outerC + innerC) * (product?.consumption || 1.0);
  }
  return 0;
}

// Будує дефолтний набір gc_planes/tags/depth для Dr. LIHTAR MVP
// (юзер не задає геометрію коробів — використовуємо розумні дефолти)
export function buildDefaultGcDimensions(technology, area, height, opts = {}) {
  const techTags = technology?.tags || [];
  const sideA = parseFloat(opts.sideA) || 0;
  const sideB = parseFloat(opts.sideB) || 0;
  const length = parseFloat(opts.length) || 0;
  const depth = sideA > 0 ? sideA : 0.2;
  const planes = [];
  const side = Math.sqrt(Math.max(area, 0.01));
  const h = length > 0 ? length : (height || 2.7);
  const a = sideA > 0 ? sideA : side;
  const b = sideB > 0 ? sideB : side;
  if (techTags.includes('gc_install') || techTags.includes('gc_install_2')) {
    planes.push({ w: side, h, type: 'wall' });
  } else if (techTags.includes('gc_column')) {
    planes.push({ w: a, h, type: 'box' }, { w: a, h, type: 'box' }, { w: b, h, type: 'box' }, { w: b, h, type: 'box' });
  } else if (techTags.includes('gc_box_p')) {
    planes.push({ w: a, h, type: 'box' }, { w: a, h, type: 'box' }, { w: b, h, type: 'wall' });
  } else if (techTags.includes('gc_box_g')) {
    planes.push({ w: a, h, type: 'box' }, { w: b, h, type: 'box' });
  } else if (techTags.includes('gc_box_h')) {
    planes.push({ w: a, h: depth, type: 'box' }, { w: a, h, type: 'wall' });
  } else if (techTags.includes('gc_niche')) {
    planes.push({ w: a, h, type: 'box' }, { w: a, h, type: 'box' }, { w: b, h, type: 'wall' });
  }
  return {
    _gcPlanes: planes,
    _gcTechTags: techTags,
    _gcDepth: depth,
    _gcShelfLen: length > 0 ? length : side,
    _gcShelfDepth: sideA > 0 ? sideA : 0.3,
    _gcShelfCount: 1,
  };
}
